import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface NewLead {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  insurance_type: string;
  street: string;
  city: string;
  state: string;
  zip_code: string;
}

interface LeadFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onLeadAdded: (lead: any) => void;
}

export const LeadFormDialog = ({ isOpen, onClose, onLeadAdded }: LeadFormDialogProps) => {
  const [newLead, setNewLead] = useState<NewLead>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    insurance_type: "",
    street: "",
    city: "",
    state: "",
    zip_code: "",
  });
  const { toast } = useToast();

  const handleInputChange = (field: keyof NewLead, value: string) => {
    setNewLead((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      const { data, error } = await supabase
        .from("quote_leads")
        .insert([
          {
            ...newLead,
            pipeline_stage: "New",
            quoted_amount: 0,
            lead_source: "Manual Entry"
          },
        ])
        .select()
        .single();

      if (error) throw error;

      onLeadAdded(data);
      onClose();
      setNewLead({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        insurance_type: "",
        street: "",
        city: "",
        state: "",
        zip_code: "",
      });

      toast({
        title: "Success",
        description: "New lead added successfully",
      });
    } catch (error: any) {
      console.error("Error adding lead:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add new lead. Please try again.",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Lead</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              placeholder="First Name"
              value={newLead.first_name}
              onChange={(e) => handleInputChange("first_name", e.target.value)}
            />
            <Input
              placeholder="Last Name"
              value={newLead.last_name}
              onChange={(e) => handleInputChange("last_name", e.target.value)}
            />
          </div>
          <Input
            placeholder="Email"
            type="email"
            value={newLead.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
          />
          <Input
            placeholder="Phone"
            value={newLead.phone}
            onChange={(e) => handleInputChange("phone", e.target.value)}
          />
          <Select
            value={newLead.insurance_type}
            onValueChange={(value) => handleInputChange("insurance_type", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Insurance Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Auto">Auto Insurance</SelectItem>
              <SelectItem value="Home">Home Insurance</SelectItem>
              <SelectItem value="Life">Life Insurance</SelectItem>
              <SelectItem value="Business">Business Insurance</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Street Address"
            value={newLead.street}
            onChange={(e) => handleInputChange("street", e.target.value)}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              placeholder="City"
              value={newLead.city}
              onChange={(e) => handleInputChange("city", e.target.value)}
            />
            <Input
              placeholder="State"
              value={newLead.state}
              onChange={(e) => handleInputChange("state", e.target.value)}
            />
          </div>
          <Input
            placeholder="ZIP Code"
            value={newLead.zip_code}
            onChange={(e) => handleInputChange("zip_code", e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="bg-[#9b87f5] hover:bg-[#7E69AB]">
            Add Lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};